import { helpers } from '../..';
import ContextSingleton from '../../__singletons__/context-singleton';
import { AnalysisConfig } from '../../helpers/analyses-config';
import { ResourceAnalysis } from '../../types/analysis';

export async function loadAndCreateAnalyses<T extends string, TResource = any>(
  config: AnalysisConfig<T, TResource>,
  resourceId: number,
  resourceType: ResourceAnalysis['item_type'],
  isSharedResource: boolean,
  resource?: TResource
): Promise<T[]> {
  try {
    const api = ContextSingleton.getInstance().api;
    // Search for existing analyses
    const resp = await api.resource_analyses.search({
      ignore_user_scope: isSharedResource,
      query: ['$and', ['item_id_ref', '=', resourceId], ['item_type', '=', resourceType]],
      include: 'id,type',
    });

    const existingTypes = ((resp.data || []) as ResourceAnalysis[]).map((a) => a.type) as T[];

    // Find missing analyses
    const toCreate = config.availableAnalyses.filter((type) => !existingTypes.includes(type)) as T[];

    // Create missing analyses using config defaults
    if (toCreate.length > 0) {
      const analysesToCreate = toCreate
        .map((type) => {
          const defaultConfig = config.default.find((d) => d.type === type);
          if (defaultConfig) {
            return {
              report_definition: defaultConfig.initialDefinition,
              item_id_ref: resourceId,
              item_type: resourceType,
              type,
              name: defaultConfig.name || type,
            };
          }
          return null;
        })
        .filter((analysis) => analysis !== null);

      if (analysesToCreate.length > 0) {
        const response = await api.combined.run({
          resource_analyses: {
            create: analysesToCreate,
          },
        });

        const createdTypes = ((response.resource_analyses?.create || []) as Array<{ data: ResourceAnalysis }>).map(
          (item) => item.data.type
        ) as T[];
        const allAnalyses = [...existingTypes, ...createdTypes];

        // Apply filter if available and resource is provided
        if (config.filterAnalyses && resource) {
          return config.filterAnalyses(resource, allAnalyses);
        }
        return allAnalyses;
      }
    }

    // Apply filter if available and resource is provided
    if (config.filterAnalyses && resource) {
      return config.filterAnalyses(resource, existingTypes);
    }
    return existingTypes;
  } catch (error) {
    throw new Error(`Error loading/creating analyses - ${helpers.parseApiError(error as any)}`);
  }
}
